// Grass and undergrowth near the camera (RALLY_GOAL.md §11): crossed-card clumps placed by a
// stable hash per 12 m cell (so they don't swim as the camera moves), denser on grass and verges,
// sparse under the trees, none on the road; scaled to nothing at the edge of the radius so they
// fade in rather than pop; wind sway in the vertex shader.
import * as THREE from 'three/webgpu';
import { positionLocal, positionGeometry, instanceIndex, hash, time, sin, vec3, float, normalViewGeometry, attribute, cameraPosition, smoothstep, uniform } from 'three/tsl';
import { MatteNodeMaterial, setEnvScale } from './matte';
import type { BuiltStage } from '../world/types';
import { setRange } from './particles';

const CELL = 12;
/** Cell slots kept in memory: enough for a 60 m radius ((2·60/12 + 2)² = 144) with room to spare. */
const POOL = 200;
const key = (cx: number, cz: number) => (cx + 32768) * 65536 + (cz + 32768);

function hash2(a: number, b: number, s: number): number {
  let h = (a * 374761393 + b * 668265263 + s * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** Three crossed vertical cards, base at y = 0, 1 m tall. */
function clumpGeometry(): THREE.BufferGeometry {
  const pos: number[] = [];
  const nor: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  for (let k = 0; k < 3; k++) {
    const a = (k / 3) * Math.PI;
    const cx = Math.cos(a) * 0.5;
    const cz = Math.sin(a) * 0.5;
    const b = pos.length / 3;
    for (const [u, v] of [
      [0, 0],
      [1, 0],
      [1, 1],
      [0, 1],
    ]) {
      const s = u * 2 - 1;
      pos.push(cx * s, v, cz * s);
      nor.push(0, 1, 0);
      uv.push(u, v);
    }
    idx.push(b, b + 1, b + 2, b, b + 2, b + 3);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  return g;
}

export class GrassField {
  /** Root for both ping-pong meshes (see FoliageSystem: never rewrite a buffer the GPU may still read). */
  readonly mesh = new THREE.Group();
  private readonly meshes: THREE.InstancedMesh[] = [];
  private readonly iposAttrs: THREE.InstancedBufferAttribute[] = [];
  private active = 0;
  private readonly stage: BuiltStage;
  // Cell cache as a fixed pool of slots (allocated once, reused least-recently-used first), so
  // driving creates no garbage: per clump an instance matrix (16), colour (3) and position (3).
  private readonly stride: number;
  private readonly mats: Float32Array;
  private readonly cols: Float32Array;
  private readonly poss: Float32Array;
  private readonly slotKey = new Float64Array(POOL);
  private readonly slotN = new Int32Array(POOL);
  private readonly slotUsed = new Int32Array(POOL);
  private readonly index = new Map<number, number>();
  private slots = 0;
  private lastX = Infinity;
  private lastZ = Infinity;
  private frame = 0;
  /** Radius (m); 0 disables. Set by the quality preset. */
  radius = 40;
  /** Clumps per m² on full grass. */
  density = 1.6;
  private readonly m4 = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly p = new THREE.Vector3();
  private readonly s = new THREE.Vector3();
  private readonly up = new THREE.Vector3(0, 1, 0);
  private readonly w = [0, 0, 0];
  private readonly fadeR = uniform(40);

  constructor(stage: BuiltStage, map: THREE.Texture, capacity = 14000) {
    this.stage = stage;
    this.stride = Math.round(CELL * CELL * this.density);
    this.mats = new Float32Array(POOL * this.stride * 16);
    this.cols = new Float32Array(POOL * this.stride * 3);
    this.poss = new Float32Array(POOL * this.stride * 3);
    const m = new MatteNodeMaterial({ map, alphaTest: 0.38, side: THREE.DoubleSide, roughness: 0.9, metalness: 0 });
    // Lit like the ground it grows from.
    m.normalNode = normalViewGeometry;
    setEnvScale(m, 0.6);
    const phase = hash(instanceIndex).mul(6.283);
    // three applies the instance matrix before positionNode: positionLocal is already the placed
    // (world) position, so the clump's own height comes from the geometry and the fade shrinks it
    // toward its base, not toward the world origin.
    const h = positionGeometry.y;
    const sway = sin(time.mul(1.7).add(phase)).mul(0.07).add(sin(time.mul(3.1).add(phase.mul(2.3))).mul(0.025));
    // Shrink to nothing toward the edge of the radius (no popping), using each clump's position.
    const ip = attribute('ipos', 'vec3');
    const d = ip.xz.sub(cameraPosition.xz).length();
    const fade = float(1).sub(smoothstep(this.fadeR.mul(0.7), this.fadeR, d));
    const base = vec3(ip.x, ip.y.sub(0.03), ip.z);
    m.positionNode = base.add(positionLocal.sub(base).mul(fade)).add(vec3(sway.mul(h).mul(h), float(0), sway.mul(h).mul(h).mul(0.7)));
    for (let k = 0; k < 2; k++) {
      const geo = clumpGeometry();
      const ipos = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
      geo.setAttribute('ipos', ipos);
      const im = new THREE.InstancedMesh(geo, m, capacity);
      im.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
      im.count = 0;
      im.frustumCulled = false;
      im.castShadow = false;
      im.receiveShadow = true;
      im.visible = false;
      this.meshes.push(im);
      this.iposAttrs.push(ipos);
      this.mesh.add(im);
    }
  }

  /** Slot holding cell (cx, cz), generating it into the least recently used slot if needed; -1 if the pool is exhausted this update. */
  private cell(cx: number, cz: number): number {
    const k = key(cx, cz);
    const hit = this.index.get(k);
    if (hit !== undefined) {
      this.slotUsed[hit] = this.frame;
      return hit;
    }
    let slot: number;
    if (this.slots < POOL) slot = this.slots++;
    else {
      slot = 0;
      let oldest = Infinity;
      for (let i = 0; i < POOL; i++) {
        if (this.slotUsed[i] < oldest) {
          oldest = this.slotUsed[i];
          slot = i;
        }
      }
      if (oldest === this.frame) return -1;
      this.index.delete(this.slotKey[slot]);
    }
    const st = this.stage;
    const tries = Math.min(this.stride, Math.round(CELL * CELL * this.density));
    const base = slot * this.stride;
    const mat = this.mats;
    const col = this.cols;
    const pos = this.poss;
    let n = base;
    for (let t = 0; t < tries; t++) {
      const x = (cx + hash2(cx * 131 + t, cz, 1)) * CELL;
      const z = (cz + hash2(cx, cz * 131 + t, 2)) * CELL;
      if (st.surfaces.surfaceAt(x, z) !== 'grass') continue;
      st.groundAt?.(x, z, this.w);
      const grass = this.w[2];
      const forest = this.w[0];
      // Not in the bare dirt band along the road edge.
      if (1 - this.w[0] - this.w[1] - this.w[2] > 0.25) continue;
      const p = grass * 1.0 + forest * 0.12;
      if (hash2(cx + t * 7, cz - t * 3, 3) > p) continue;
      const y = st.visualHeightAt ? st.visualHeightAt(x, z) : st.heightAt(x, z);
      const r = hash2(t, cx + cz, 4);
      const sc = (0.45 + 0.5 * hash2(t, cz - cx, 5)) * (forest > grass ? 0.8 : 1);
      this.p.set(x, y - 0.03, z);
      this.q.setFromAxisAngle(this.up, r * Math.PI * 2);
      this.s.set(sc * 1.4, sc, sc * 1.4);
      this.m4.compose(this.p, this.q, this.s);
      this.m4.toArray(mat, n * 16);
      // Colour variation: greener in the open, drier/yellower in patches.
      const dry = hash2(Math.floor(x / 9), Math.floor(z / 9), 6);
      // Darker under the trees (shaded, and the blades there are thinner and greener).
      const shade = 0.78 - 0.22 * forest;
      col[n * 3] = (0.82 + 0.25 * dry) * shade;
      col[n * 3 + 1] = (0.88 + 0.12 * (1 - dry)) * shade;
      col[n * 3 + 2] = (0.72 + 0.1 * dry) * shade;
      pos[n * 3] = x;
      pos[n * 3 + 1] = y;
      pos[n * 3 + 2] = z;
      n++;
    }
    this.slotKey[slot] = k;
    this.slotN[slot] = n - base;
    this.slotUsed[slot] = this.frame;
    this.index.set(k, slot);
    return slot;
  }

  update(camX: number, camZ: number): void {
    this.frame++;
    if (this.radius <= 0) {
      this.mesh.visible = false;
      return;
    }
    this.mesh.visible = true;
    if (Math.hypot(camX - this.lastX, camZ - this.lastZ) < 2.5) return;
    this.lastX = camX;
    this.lastZ = camZ;
    const R = this.radius;
    const R2 = R * R;
    this.fadeR.value = R;
    const target = 1 - this.active;
    const im = this.meshes[target];
    const iposAttr = this.iposAttrs[target];
    const ipos = iposAttr.array as Float32Array;
    const mat = im.instanceMatrix.array as Float32Array;
    const col = im.instanceColor!.array as Float32Array;
    const cap = im.instanceMatrix.count;
    let k = 0;
    const c0 = Math.floor((camX - R) / CELL);
    const c1 = Math.floor((camX + R) / CELL);
    const r0 = Math.floor((camZ - R) / CELL);
    const r1 = Math.floor((camZ + R) / CELL);
    for (let cx = c0; cx <= c1 && k < cap; cx++) {
      for (let cz = r0; cz <= r1 && k < cap; cz++) {
        const slot = this.cell(cx, cz);
        if (slot < 0) continue;
        const j0 = slot * this.stride;
        const j1 = j0 + this.slotN[slot];
        const sm = this.mats;
        const sc = this.cols;
        const sp = this.poss;
        for (let j = j0; j < j1 && k < cap; j++) {
          const dx = sp[j * 3] - camX;
          const dz = sp[j * 3 + 2] - camZ;
          if (dx * dx + dz * dz > R2) continue;
          for (let e = 0, o = k * 16, i0 = j * 16; e < 16; e++) mat[o + e] = sm[i0 + e];
          col[k * 3] = sc[j * 3];
          col[k * 3 + 1] = sc[j * 3 + 1];
          col[k * 3 + 2] = sc[j * 3 + 2];
          ipos[k * 3] = sp[j * 3];
          ipos[k * 3 + 1] = sp[j * 3 + 1];
          ipos[k * 3 + 2] = sp[j * 3 + 2];
          k++;
        }
      }
    }
    im.count = k;
    setRange(im.instanceMatrix, k * 16);
    setRange(im.instanceColor!, k * 3);
    setRange(iposAttr, k * 3);
    this.meshes[this.active].visible = false;
    im.visible = true;
    this.active = target;
  }
}
